import { api } from './client';

/**
 * The offers a customer can see before typing anything.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * `src/data/booking.ts` used to carry `coupons: Coupon[]` - a hardcoded list,
 * checked against a hardcoded minimum, applied by arithmetic this app did to
 * its own total. None of it was real: there was no offers endpoint and no
 * server-side redemption, so "Apply" took money off a bill the server had
 * never agreed to reduce. That file's own docblock says as much and asks for
 * exactly this module.
 *
 * This is the READ half only - what to show in the list. It does not say
 * whether an offer applies to any particular basket, or for how much: that
 * question belongs to `POST checkout/quote` (see `services/checkout.ts`),
 * which is asked fresh every time the basket, the code or the coins change.
 * A listed offer can still be refused at quote time - below the minimum, the
 * wrong city, already used once - and the refusal there is what the cart
 * shows, not anything worked out from this list.
 */
export type Discount = {
  /*
   * There is deliberately no `name`.
   *
   * A rule's `name` is what ops calls the campaign among themselves ("Test
   * coupon 200") - the console prints "internal only, never shown to a
   * customer" beneath that field as it is typed. It used to be sent and
   * drawn as this card's heading, which put an operator's shorthand in front
   * of the customer. The server no longer sends it.
   */
  /**
   * "₹200 off your first booking" - written by the backend to be shown to the
   * customer VERBATIM, and the whole of what an offer card says. Do not
   * re-word or re-derive this from the numbers.
   */
  description: string;
  /** What to type to claim it. Null for an automatic offer - nothing to type,
      the quote applies it on its own when the basket qualifies. */
  code: string | null;
  /**
   * The minimum basket, as a decimal string, or null when there is none.
   *
   * A STRING, like every money value this app reads off the server - see the
   * note in `services/wallet.ts`. Used only to soften an offer's card when the
   * basket looks short (a plain comparison, not a discount calculation); the
   * authoritative answer is always the quote's `codeRefusal`.
   */
  minOrderValue: string | null;
  /** ISO date the offer stops being listed, or null for no end in sight. */
  endsAt: string | null;
};

/** `GET /discounts/available` - the customer's own offers, bearer-authed. */
export const fetchAvailableDiscounts = (signal?: AbortSignal) =>
  api.get<Discount[]>('/discounts/available', { signal });
